import { Injectable, inject } from '@angular/core';
import { InvestigationsState } from '../../states/investigations.state';
import { InvestigationAttachmentEntity } from '../../../domain/entities/investigation-attachment.entity';
import { GetInvestigationsStateResult } from './get-investigations-state.result';

/**
 * GetInvestigationsState use case.
 * Provides read-only access to Investigations state for presentation layer.
 * This maintains Clean Architecture by preventing direct State injection in presentation.
 */
@Injectable({ providedIn: 'root' })
export class GetInvestigationsStateUseCase {
  private readonly _state = inject(InvestigationsState);

  public execute(): GetInvestigationsStateResult {
    const state = this._state;
    return {
      // State mutation methods
      setLoadingAttachments: (loading: boolean) => state.setLoadingAttachments(loading),
      setAttachments: (attachments: InvestigationAttachmentEntity[]) => state.setAttachments(attachments),
      addAttachment: (attachment: InvestigationAttachmentEntity) => state.addAttachment(attachment),
      removeAttachment: (id: string) => state.removeAttachment(id),
      setDeletingAttachmentId: (id: string | null) => state.setDeletingAttachmentId(id),
      // Investigation
      selectedInvestigation: this._state.selectedInvestigation,
      loadingInvestigation: this._state.loadingInvestigation,
      errorLoadingInvestigation: this._state.errorLoadingInvestigation,
      savingInvestigation: this._state.savingInvestigation,
      errorInvestigation: this._state.errorInvestigation,
      // Employees
      employees: this._state.employees,
      loadingEmployees: this._state.loadingEmployees,
      errorEmployees: this._state.errorEmployees,
      // Status & Priority
      statusSnapshots: this._state.statusSnapshots,
      loadingStatusSnapshots: this._state.loadingStatusSnapshots,
      errorStatusSnapshots: this._state.errorStatusSnapshots,
      prioritySnapshots: this._state.prioritySnapshots,
      loadingPrioritySnapshots: this._state.loadingPrioritySnapshots,
      errorPrioritySnapshots: this._state.errorPrioritySnapshots,
      savingStatus: this._state.savingStatus,
      savingPriority: this._state.savingPriority,
      savingAssignees: this._state.savingAssignees,
      // Field values
      fieldValues: this._state.fieldValues,
      loadingFieldValues: this._state.loadingFieldValues,
      errorFieldValues: this._state.errorFieldValues,
      savingFieldValues: this._state.savingFieldValues,
      // Attachments
      attachments: this._state.attachments,
      loadingAttachments: this._state.loadingAttachments,
      uploadingAttachment: this._state.uploadingAttachment,
      deletingAttachmentId: this._state.deletingAttachmentId,
      errorAttachments: this._state.errorAttachments,
      // Involved employees
      involvedEmployees: this._state.involvedEmployees,
      loadingInvolvedEmployees: this._state.loadingInvolvedEmployees,
      errorInvolvedEmployees: this._state.errorInvolvedEmployees,
      // Watchers
      watchers: this._state.watchers,
      loadingWatchers: this._state.loadingWatchers,
      // Messages
      messages: this._state.messages,
      loadingMessages: this._state.loadingMessages,
      errorMessages: this._state.errorMessages,
      addingMessage: this._state.addingMessage,
      // Work notes
      workNotes: this._state.workNotes,
      loadingWorkNotes: this._state.loadingWorkNotes,
      errorWorkNotes: this._state.errorWorkNotes,
      addingWorkNote: this._state.addingWorkNote,
      // Linked investigations
      linkedInvestigations: this._state.linkedInvestigations,
      loadingLinkedInvestigations: this._state.loadingLinkedInvestigations,
      errorLinkedInvestigations: this._state.errorLinkedInvestigations,
      linkingInvestigation: this._state.linkingInvestigation,
      unlinkingInvestigation: this._state.unlinkingInvestigation,
      // Checklist steps
      checklistSteps: this._state.checklistSteps,
      loadingChecklistSteps: this._state.loadingChecklistSteps,
      errorChecklistSteps: this._state.errorChecklistSteps,
      updatingChecklistStep: this._state.updatingChecklistStep,
      // Email template snapshots
      emailTemplateSnapshots: this._state.emailTemplateSnapshots,
      loadingEmailTemplateSnapshots: this._state.loadingEmailTemplateSnapshots,
      errorEmailTemplateSnapshots: this._state.errorEmailTemplateSnapshots,
      // Parent/Child investigations
      parentChildInvestigations: this._state.parentChildInvestigations,
      setParentChildInvestigations: (data) => state.setParentChildInvestigations(data),
      // Transferred case fields (HCMI-796)
      transferredCaseFields: this._state.transferredCaseFields,
      loadingTransferredCaseFields: this._state.loadingTransferredCaseFields,
      errorTransferredCaseFields: this._state.errorTransferredCaseFields,
      changingInvestigationType: this._state.changingInvestigationType,
    };
  }
}
